/**
 * Event Group Utilities
 *
 * Shared business logic for handling festival event groups (pre-parties, after-parties, etc.)
 * Used across web and mobile platforms.
 */

export type RelationshipType =
  | 'official_after_party'
  | 'recovery_party'
  | 'pre_party'
  | 'unofficial_after_party';

export type EventType =
  | 'main_festival'
  | 'pre_party'
  | 'after_party'
  | 'recovery_party'
  | 'official_after_party'
  | 'unofficial_after_party';

/**
 * Maps event group URL parameters to database relationship types
 */
export const EVENT_GROUP_TO_RELATIONSHIP: Record<string, RelationshipType> = {
  official_after_party: 'official_after_party',
  recovery_party: 'recovery_party',
  pre_party: 'pre_party',
  unofficial_after_party: 'unofficial_after_party',
};

/**
 * Maps event types to event group URL parameters
 * main_festival returns undefined (no eventGroup param needed)
 */
export const EVENT_TYPE_TO_GROUP: Record<EventType, string | undefined> = {
  main_festival: undefined,
  pre_party: 'pre_party',
  after_party: 'after_party',
  recovery_party: 'recovery_party',
  official_after_party: 'official_after_party',
  unofficial_after_party: 'unofficial_after_party',
};

/**
 * Icon mappings for event types (using Remix Icon names)
 */
export const EVENT_GROUP_ICONS: Record<EventType, string> = {
  main_festival: 'rainbow-line',
  pre_party: 'sun-line',
  after_party: 'moon-line',
  recovery_party: 'moon-line',
  official_after_party: 'moon-line',
  unofficial_after_party: 'moon-line',
};

/**
 * Converts an event group URL parameter to a database relationship type
 *
 * @param groupId - The event group from URL (e.g., 'official_after_party')
 * @returns The corresponding relationship type, or undefined if not found
 */
export function mapGroupIdToRelationshipType(groupId: string): RelationshipType | undefined {
  return EVENT_GROUP_TO_RELATIONSHIP[groupId];
}

/**
 * Determines if an event group should use the show list view
 * (different-venue events) vs integrated view (same-venue events)
 *
 * Supports both exact matches and indexed patterns (e.g., "after_party_2")
 * for handling multiple show_list events of the same type.
 *
 * @param eventGroup - The event group from URL
 * @returns true if this event group should display as a show list
 */
export function isShowListEventGroup(eventGroup: string | null | undefined): boolean {
  if (!eventGroup) return false;

  // Check exact matches
  if (eventGroup === 'recovery_party' ||
      eventGroup === 'official_after_party' ||
      eventGroup === 'unofficial_after_party') {
    return true;
  }

  // Check indexed patterns (e.g., "after_party_2", "pre_party_1")
  // These are used when there are multiple show_list events of the same type
  return eventGroup.startsWith('after_party_') ||
         eventGroup.startsWith('pre_party_') ||
         eventGroup.startsWith('recovery_party_') ||
         eventGroup.startsWith('official_after_party_') ||
         eventGroup.startsWith('unofficial_after_party_');
}

/**
 * Converts an event type to the corresponding URL event group parameter
 *
 * @param eventType - The event type
 * @returns The event group parameter for the URL, or undefined for main_festival
 */
export function mapEventTypeToGroup(eventType: EventType): string | undefined {
  return EVENT_TYPE_TO_GROUP[eventType];
}

/**
 * Gets the display label for an event type, with support for custom labels
 *
 * @param eventType - The event type
 * @param festivalDetails - Optional festival details containing custom labels
 * @returns The display label for the event type
 */
export function getEventTypeLabel(
  eventType: EventType | string,
  festivalDetails?: {
    pre_party_name?: string | null;
    after_party_name?: string | null;
  }
): string {
  const labels: Record<string, string> = {
    main_festival: 'Festival',
    pre_party: festivalDetails?.pre_party_name || 'Pre-Party',
    after_party: festivalDetails?.after_party_name || 'After-Party',
    recovery_party: festivalDetails?.after_party_name || 'Recovery Party', // Recovery party uses after_party_name
    official_after_party: festivalDetails?.after_party_name || 'Official After-Party',
    unofficial_after_party: 'Unofficial After-Party',
  };

  return labels[eventType] || eventType;
}

/**
 * Gets the icon name for an event type
 *
 * @param eventType - The event type
 * @returns The Remix Icon name
 */
export function getEventTypeIcon(eventType: EventType | string): string {
  return EVENT_GROUP_ICONS[eventType as EventType] || 'calendar-event-line';
}

/**
 * Strips the "Official " prefix from labels if present
 * Used for compact displays where space is limited
 *
 * @param label - The label to process
 * @returns The label with "Official " removed if present
 */
export function stripOfficialPrefix(label: string): string {
  return label.replace(/^Official\s+/i, '');
}
